import React, {useState, useContext, useMemo, useEffect, useCallback} from 'react';
import {ConfirmLeave} from '@albert-io/atomic';
import {ContentDiscoveryContext} from '../../ContentDiscoveryProvider.react';

import {
  SaveDraftModal,
  SaveWithNameInputModal,
  warnUser,
  DiscardModal
} from './NavigationModals.react';

interface ConfirmLeaveWrapperProps {
  draftName: string;
  unsavedNameChanges: boolean;
  route: string | {path: string};
  openFolderModal: () => void;
  newFolder: any;
  onReset: () => void;
  showAssignmentSharing: boolean;
}

const ConfirmLeaveWrapper = ({
  draftName,
  unsavedNameChanges,
  route,
  openFolderModal,
  newFolder,
  onReset,
  showAssignmentSharing
}: ConfirmLeaveWrapperProps) => {
  const {
    assignmentResults,
    hasUnsavedChanges,
    draftHasPendingChanges,
    draftAssignment,
    selectedQuestionSets,
    hideSaving
  } = useContext(ContentDiscoveryContext);

  const [assignmentBuilderWarning, setAssignmentBuilderWarning] = useState(false);

  const [warningModalIsOpen, setWarningModalIsOpen] = useState(false); // children set to true once warning modals have mounted

  const blankAssignment = useMemo(() => {
    return (
      !draftAssignment &&
      draftName.length === 0 &&
      !draftHasPendingChanges &&
      selectedQuestionSets.size === 0
    );
  }, [draftAssignment, draftName, selectedQuestionSets, draftHasPendingChanges]);

  useEffect(() => {
    window.addEventListener('beforeunload', warnUser);
    const noChanges = !(unsavedNameChanges || hasUnsavedChanges);
    if (blankAssignment || noChanges) {
      window.removeEventListener('beforeunload', warnUser);
    }

    return () => {
      window.removeEventListener('beforeunload', warnUser);
    };
  }, [unsavedNameChanges, hasUnsavedChanges, blankAssignment]);

  const showModal = useMemo(() => {
    if (assignmentResults.size > 0) return false;
    return warningModalIsOpen || (!blankAssignment && (unsavedNameChanges || hasUnsavedChanges));
  }, [
    hasUnsavedChanges,
    warningModalIsOpen,
    blankAssignment,
    unsavedNameChanges,
    assignmentResults
  ]);

  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.id === 'beta-button_nav') {
        if (showAssignmentSharing || blankAssignment) {
          onReset();
        } else {
          setAssignmentBuilderWarning(true);
        }
      }
    };
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [onReset, showAssignmentSharing, blankAssignment]);

  return (
    <ErrorBoundary>
      {showModal && !hideSaving && (
        <ConfirmLeaveHandler
          draftName={draftName}
          setWarningModalIsOpen={setWarningModalIsOpen}
          route={route}
        />
      )}
      {showModal && hideSaving && newFolder === null && (
        <NoSaveConfirmLeaveHandler route={route} openFolderModal={openFolderModal} />
      )}
      {assignmentBuilderWarning && (
        <DiscardModal
          dismiss={() => setAssignmentBuilderWarning(false)}
          confirmLeave={() => {
            onReset();
            setAssignmentBuilderWarning(false);
          }}
          openFolderModal={openFolderModal}
        />
      )}
    </ErrorBoundary>
  );
};

export default ConfirmLeaveWrapper;

interface ConfirmLeaveHandlerProps {
  route: string | {path: string};
  draftName: string;
  setWarningModalIsOpen: any;
}

export const ConfirmLeaveHandler = ({
  route,
  draftName,
  setWarningModalIsOpen
}: ConfirmLeaveHandlerProps) => {
  const [showSaveModal, setShowSaveModal] = useState(true);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<null | any>(null);
  const [waitingForManualSave, setWaitingForManualSave] = useState(false);

  const {
    autoSave,
    draftHasPendingChanges,
    draftHasSavedDuringSession,
    manualSave,
    draftSaveError,
    setDraftSaveError
  } = useContext(ContentDiscoveryContext);

  const hasUnsavedChanges = useMemo(() => {
    if (draftHasPendingChanges) return true;
    return !draftHasSavedDuringSession;
  }, [draftHasPendingChanges, draftHasSavedDuringSession]);

  const handleSave = useCallback(
    // Used for 'Save a draft' and 'Stay on page' button
    (confirmLeave, dismiss) => {
      if (draftSaveError) {
        // Stay on page
        setDraftSaveError(null); // TODO: investigate whether this is needed
        dismiss();
        return;
      }
      // Save a draft scenarios
      if (!hasUnsavedChanges && autoSave) {
        // Has no unsaved changes when clicking save, can just leave
        confirmLeave();
      } else if (draftName.length > 0 && !autoSave) {
        // saving for first time, and has a draft name
        setWaitingForManualSave(true);
        manualSave(draftName);
        setPendingLeave(() => confirmLeave); // syntax to store function in set state
      } else if (!autoSave && draftName.length === 0) {
        //  saving for first time and no draft name
        setShowNameModal(true);
        setPendingLeave(() => confirmLeave);
      } else {
        // has pending changes and auto save is on
        // defer dismissal to useEffect
        setPendingLeave(() => confirmLeave);
      }
    },
    [hasUnsavedChanges, draftName, manualSave, autoSave, draftSaveError, setDraftSaveError]
  );

  const handleNameModalSave = useCallback(
    (name) => {
      manualSave(name);
      setWaitingForManualSave(true);
    },
    [manualSave, setWaitingForManualSave]
  );
  // manual save confirm leave handling
  useEffect(() => {
    if (waitingForManualSave && draftHasSavedDuringSession && pendingLeave !== null) {
      pendingLeave();
    }
    if (waitingForManualSave && draftSaveError !== null) {
      setShowSaveModal(true);
    }
  }, [draftHasSavedDuringSession, pendingLeave, waitingForManualSave, draftSaveError]);

  // useAutosave confirm leave handling
  useEffect(() => {
    if (autoSave && !draftHasPendingChanges && pendingLeave !== null) {
      pendingLeave();
    }
    if (autoSave && draftSaveError) {
      setShowSaveModal(true);
    }
  }, [autoSave, draftHasPendingChanges, pendingLeave, draftSaveError]);

  const onMountHandler = useCallback(() => {
    setWarningModalIsOpen(true);
  }, [setWarningModalIsOpen]);

  return (
    <ConfirmLeave route={route}>
      {({dismiss, confirmLeave}) => (
        <>
          {showSaveModal && !showNameModal && (
            <SaveDraftModal
              onMount={onMountHandler}
              confirmLeave={confirmLeave}
              dismiss={dismiss}
              saveError={draftSaveError}
              handleSave={() => handleSave(confirmLeave, dismiss)}
            />
          )}
          {showNameModal && (
            <SaveWithNameInputModal onSave={handleNameModalSave} onDismiss={() => confirmLeave()} />
          )}
        </>
      )}
    </ConfirmLeave>
  );
};

interface NoSaveConfirmLeaveProps {
  route: string | {path: string};
  openFolderModal: () => void;
}

const NoSaveConfirmLeaveHandler = ({route, openFolderModal}: NoSaveConfirmLeaveProps) => {
  return (
    <ConfirmLeave route={route}>
      {({dismiss, confirmLeave}) => (
        <DiscardModal
          dismiss={dismiss}
          confirmLeave={confirmLeave}
          openFolderModal={openFolderModal}
        />
      )}
    </ConfirmLeave>
  );
};

class ErrorBoundary extends React.Component<any, any> {
  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return {hasError: true};
  }

  constructor(props: any) {
    super(props);
    this.state = {hasError: false};
  }

  componentDidCatch(error, errorInfo) {
    this.setState({hasError: true});
    console.log(error, errorInfo);

    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}
